import { toTenantDate } from '@utils/timeZone';
import {
  set,
  getDate,
  getMinutes,
  setMinutes,
  getMonth,
  getYear,
  getHours as getHourOfDay,
  isBefore,
  isAfter,
  addDays,
  differenceInCalendarDays,
} from 'date-fns';
import { getDefaultEndsAt, getDefaultStartsAt, initialState } from './constants';
import { Job, JobTask, Schedule } from '@root/globalTypes';
import { getFirstHour, getLastHour } from '@components/scheduler/grid/helpers';
import { SchedulerSegment } from './types';

export const createSchedule = (task: JobTask, data?: Partial<Job>): Schedule => ({
  ...initialState.active.schedule,
  id: 'new',
  task: {
    ...initialState.active.schedule.task,
    ...task,
    job: {
      id: data?.id || '',
      projectId: data?.projectId || '',
      name: data?.name || '',
      address: data?.address || '',
      division: data?.division || { id: '', name: '' },
      project: {
        name: data?.project?.name,
        address: data?.project?.address,
      },
    },
  },
});

export const setDatesForCurrentDay = (date?: Date, startsAt?: string, endsAt?: string) => {
  if (!date) return { startsAt: '', endsAt: '' };
  if (!startsAt || !endsAt) return { startsAt: getDefaultStartsAt(), endsAt: getDefaultEndsAt() };

  const parsedStartsAt = toTenantDate(startsAt);
  const parsedEndsAt = toTenantDate(endsAt);
  const endsAtDayOffset = differenceInCalendarDays(parsedEndsAt, parsedStartsAt);
  const endsAtDate = endsAtDayOffset ? addDays(date, endsAtDayOffset) : date;

  return {
    startsAt: set(parsedStartsAt, {
      date: getDate(date),
      month: getMonth(date),
      year: getYear(date),
    }).toISOString(),
    endsAt: set(parsedEndsAt, {
      date: getDate(endsAtDate),
      month: getMonth(endsAtDate),
      year: getYear(endsAtDate),
    }).toISOString(),
  };
};

export const roundDate = (
  date: string | undefined,
  segment: SchedulerSegment,
  hours?: Date[][],
) => {
  if (!date || !hours) return undefined;

  const parsedDate = toTenantDate(date);
  const firstHour = getFirstHour(parsedDate, getHourOfDay(hours[0][0]));
  const lastHour = getLastHour(hours, segment);
  const minutes = getMinutes(parsedDate);
  const minutesToSegment = minutes % segment.length;

  const newDate = setMinutes(
    parsedDate,
    minutesToSegment > 7
      ? minutes + (segment.length - minutesToSegment)
      : minutes - minutesToSegment,
  );

  if (isBefore(newDate, firstHour)) return firstHour;
  if (isAfter(newDate, lastHour)) return lastHour;
  return newDate;
};

export const roundSchedulesDates = (
  data: Schedule[],
  segment: SchedulerSegment,
  hours: Date[][],
): Schedule[] =>
  data.map((el) => ({
    ...el,
    roundedStartsAt: roundDate(el.startsAt, segment, hours)?.toISOString() || '',
    roundedEndsAt: roundDate(el.endsAt, segment, hours)?.toISOString() || '',
  }));
